import { useCallback, useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { fetchFastApi } from "../api/fastapi";
import { consumePostLoginRedirect } from "../lib/authRedirect";
import { Link } from "react-router-dom";
import { OptroDomainDialog } from "../components/auth/OptroDomainDialog";
import { useSignInWithOptroEnabled } from "../hooks/useSignInWithOptroEnabled";
import { useOptroReferrerAutoSignIn } from "../hooks/useOptroReferrerAutoSignIn";
import { isSignInWithOptroOverride } from "../lib/optroDomain";
import { capturedOptroReferrer } from "../lib/optroReferrer";
import { getSystemIconPath } from "../lib/systemOfRecord";

export const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [optroDialogOpen, setOptroDialogOpen] = useState(false);
  const optroSignInEnabled = useSignInWithOptroEnabled();
  // Either alone surfaces the entry point. The LaunchDarkly flag is how the
  // feature rolls out; ?signInWithOptro=1 is the internal preview before it
  // does. OR rather than AND because this page is always anonymous —
  // LaunchDarklyIdentify re-identifies only after auth — so the flag here can
  // only ever evaluate the anonymous context and never reflects the workspace
  // targeting it is written against. The backend does not gate
  // /auth/optrooauth/* on the flag; its base-domain allowlist is the boundary.
  // A tab that arrived from an Optro instance also gets the entry point, so a
  // failed auto sign-in (below) still leaves a way back in.
  // Read once on mount — the URL cannot change without remounting this page.
  const [optroOverride] = useState(isSignInWithOptroOverride);
  const [arrivedFromOptro] = useState(() => capturedOptroReferrer() !== null);
  const showOptroSignIn =
    optroSignInEnabled || optroOverride || arrivedFromOptro;

  // Bounces straight into Optro sign-in when this tab arrived from a known
  // Optro/AuditBoard domain, instead of making the user click through the
  // manual dialog.
  useOptroReferrerAutoSignIn();

  useEffect(() => {
    //clear local storage
    localStorage.clear();
  }, []);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!email || !password) {
        setError("Please enter your email and password");
        return;
      }
      try {
        setLoading(true);
        const res = await fetchFastApi({
          body: { email, password },
          method: "POST",
          path: "/auth/",
        });
        if (res.error_message) {
          setError(res.error_message);
          return;
        }
        if (res.access_token) {
          localStorage.setItem("access_token", res.access_token);
          localStorage.setItem("refresh_token", res.refresh_token);
          localStorage.setItem("id_token", res.id_token);
          localStorage.setItem("email", email);
          window.location.href = consumePostLoginRedirect("/audit");
        }
      } catch (e) {
        console.error("Error during email sign-in:", e);
        setError("An error occurred during sign-in. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [password, email],
  );

  const handleOptroSignIn = useCallback(() => {
    // Open the Optro domain prompt. The dialog resolves the instance base URL
    // (localhost:9002 for local dev, else <sub>.auditboardapp.com), clears the
    // Google g_state cookie on localhost, and hands off to the backend kickoff.
    setOptroDialogOpen(true);
  }, []);

  return (
    <div className="h-screen w-screen bg-[url('/gradient_bg.png')] bg-no-repeat bg-top bg-cover">
      <div className="flex h-screen justify-center items-center">
        <div className="bg-white p-8 rounded-sm shadow-2xl z-10 w-96 border border-stone-100">
          <form onSubmit={handleEmailSubmit}>
            <div className="flex flex-col gap-4">
              <img src="/logo_white_bg.svg" alt="logo" className="w-20" />
              <div>
                <div className="text-2xl font-semibold">Welcome to Midship</div>
                <div className="text-font-secondary">
                  Please sign in or sign up below.
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-sm font-semibold">
                  Email
                </label>
                <Input
                  type="email"
                  id="email"
                  placeholder="Email"
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  autoComplete="email"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="password" className="text-sm font-semibold">
                  Password
                </label>
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  id="password"
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </div>
              <Link
                to={`/reset-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                className="text-sm underline"
              >
                Forgot your password?
              </Link>
              <div data-testid="sign-in-error" className="text-sm text-red-500">
                {error}
              </div>
              <Button loading={loading} type="submit">
                Continue with Email
              </Button>
              {showOptroSignIn && (
                <>
                  <div className="w-full border-t border-stone-200"></div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleOptroSignIn}
                    className="relative"
                  >
                    {/* Pinned to the left edge (out of flow) so the label stays
                        centered in the full button width. */}
                    <img
                      src={getSystemIconPath("auditboard")}
                      alt=""
                      aria-hidden="true"
                      className="absolute left-3 h-4 w-4"
                    />
                    Sign in with Optro
                  </Button>
                  <OptroDomainDialog
                    open={optroDialogOpen}
                    onOpenChange={setOptroDialogOpen}
                  />
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
